import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Token is added via auth store
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const chatApi = {
  sendMessage: (message, conversationId, fileIds = [], comfortLanguage = null, comfortState = null) =>
    api.post('/chat', { message, conversationId, fileIds, comfortLanguage, comfortState }),

  getConversations: (limit = 20, offset = 0) =>
    api.get(`/chat/conversations?limit=${limit}&offset=${offset}`),

  getConversation: (id) =>
    api.get(`/chat/conversations/${id}`),

  deleteConversation: (id) =>
    api.delete(`/chat/conversations/${id}`),

  renameConversation: (id, title) =>
    api.patch(`/chat/conversations/${id}`, { title }),

  getQuota: () =>
    api.get('/chat/quota'),

  getMemoryProfile: () =>
    api.get('/chat/memory-profile'),

  saveMemoryProfile: (data) =>
    api.put('/chat/memory-profile', data),

  generateReviewList: (content) =>
    api.post('/chat/review-list', { content }),

  generateMindMapImage: (content) =>
    api.post('/chat/generate-mindmap-image', { content }, { timeout: 90000 }),

  chargeVisualMindMap: () =>
    api.post('/chat/charge-visual-mindmap'),

  // Pictogram from the GPU pictogram service; first request for a question takes 6–20 s.
  createPictogram: (question) =>
    api.post('/chat/pictogram', { question }, { timeout: 60000 }),

  // Mental Summary Chart image — only used by the mental-summary-charts technique.
  // userQuery is the student's original message that produced this response —
  // helps the extractor honour "use Tom & Jerry" even if the AI's response
  // wording drifted to something else.
  generateMscImage: (content, userQuery) =>
    api.post('/techniques/generate-msc-image', { content, userQuery }, { timeout: 90000 }),
};

export const paymentApi = {
  getPackages: () =>
    api.get('/payments/packages'),

  createRechargeOrder: (packageId, couponCode = null) =>
    api.post('/payments/recharge/create-order', { packageId, couponCode }),

  verifyRecharge: (data) =>
    api.post('/payments/recharge/verify', data),

  verifyPayment: (payment_request_id, payment_id) =>
    api.post('/payments/verify', { payment_request_id, payment_id }),

  getHistory: (limit = 20, offset = 0) =>
    api.get(`/payments/history?limit=${limit}&offset=${offset}`),

  getTopupConfig: () =>
    api.get('/payments/topup/config'),

  createTopupOrder: (amount, couponCode = null) =>
    api.post('/payments/topup/create-order', { amount, couponCode }),

  verifyTopup: (data) =>
    api.post('/payments/topup/verify', data),

  getRegistrationFee: () =>
    api.get('/payments/registration/fee'),

  createRegistrationOrder: (couponCode = null) =>
    api.post('/payments/registration/create-order', { couponCode })
};

export const studentApi = {
  getProfile: () =>
    api.get('/student/profile'),

  updateProfile: (data) =>
    api.put('/student/profile', data),

  getUsage: () =>
    api.get('/student/usage'),

  getProgress: () =>
    api.get('/student/progress'),

  getQuota: () =>
    api.get('/student/quota'),

  getRechargeHistory: () =>
    api.get('/student/recharge-history'),

  getDailyUsage: (days = 30) =>
    api.get(`/student/daily-usage?days=${days}`),

  getActivityCalendar: (days = 30) =>
    api.get(`/student/activity-calendar?days=${days}`)
};

export const trainerApi = {
  getDashboard: () =>
    api.get('/trainer/dashboard'),

  getStudents: (limit = 50, offset = 0) =>
    api.get(`/trainer/students?limit=${limit}&offset=${offset}`),

  getStudent: (id) =>
    api.get(`/trainer/students/${id}`),

  getEarnings: () =>
    api.get('/trainer/earnings'),

  getPayoutDetails: () =>
    api.get('/trainer/payout-details'),

  updatePayoutDetails: (data) =>
    api.put('/trainer/payout-details', data),

  createStudent: (studentData) =>
    api.post('/trainer/students', studentData),

  createOnboardingOrder: (studentData) =>
    api.post('/payments/onboarding/create-order', studentData),

  verifyOnboarding: (data) =>
    api.post('/payments/onboarding/verify', data),

  getL1Config: () =>
    api.get('/trainer/l1-config'),

  updateL1Config: (data) =>
    api.put('/trainer/l1-config', data),

  grantL1Trial: (studentId) =>
    api.post(`/trainer/students/${studentId}/l1`),

  updateStudentStatus: (studentId, status) =>
    api.put(`/trainer/students/${studentId}/status`, { status }),

  getEarningsBreakdown: (month) =>
    api.get(`/trainer/earnings-breakdown?month=${month}`),

  getEarningsHistory: () =>
    api.get('/trainer/earnings-history')
};

export const adminApi = {
  getDashboard: () =>
    api.get('/admin/dashboard'),

  getTrainers: () =>
    api.get('/admin/trainers'),

  createTrainer: (data) =>
    api.post('/admin/trainers', data),

  updateTrainer: (id, data) =>
    api.put(`/admin/trainers/${id}`, data),

  deleteTrainer: (id) =>
    api.delete(`/admin/trainers/${id}`),

  getStudents: (params = {}) =>
    api.get('/admin/students', { params }),

  getStudentCounts: () =>
    api.get('/admin/students/counts'),

  createStudent: (data) =>
    api.post('/admin/students', data),

  grantL1Trial: (studentId) =>
    api.post(`/admin/students/${studentId}/l1`),

  updateStudentStatus: (studentId, status) =>
    api.put(`/admin/students/${studentId}/status`, { status }),

  updateTrainerStatus: (trainerId, status) =>
    api.put(`/admin/trainers/${trainerId}/status`, { status }),

  getConfig: () =>
    api.get('/admin/config'),

  updateConfig: (data) =>
    api.put('/admin/config', data),

  // New API manual billing ledger
  getApiBilling: () =>
    api.get('/admin/api-billing'),

  addApiBillingEntry: (data) =>
    api.post('/admin/api-billing', data),

  deleteApiBillingEntry: (id) =>
    api.delete(`/admin/api-billing/${id}`),

  getRevenue: (months = 6) =>
    api.get(`/admin/revenue?months=${months}`),

  getPendingPayouts: () =>
    api.get('/admin/payouts/pending'),

  processPayout: (data) =>
    api.post('/admin/payouts/process', data),

  getStudentsSummary: () =>
    api.get('/admin/dashboard/students-summary'),

  addStudentQuestions: (studentId, questions) =>
    api.post(`/admin/students/${studentId}/add-questions`, { questions }),

  addTrainerQuestions: (trainerId, questions) =>
    api.post(`/admin/trainers/${trainerId}/add-questions`, { questions }),

  getStuckPayments: () =>
    api.get('/admin/payments/stuck'),

  recoverPayment: (id) =>
    api.post(`/admin/payments/${id}/recover`),

  getRevenueBreakdown: (month) =>
    api.get('/admin/dashboard/revenue-breakdown', { params: month ? { month } : {} }),

  getUsageBreakdown: () =>
    api.get('/admin/dashboard/usage-breakdown'),

  getRevenueAnalytics: (year, view = 'monthly') =>
    api.get(`/admin/revenue-analytics?year=${year}&view=${view}`).then(r => r.data),

  testTechnique: (id, message, fileIds = []) =>
    api.post(`/admin/techniques/${id}/test`, { message, fileIds }),

  getUsageAnalytics: () =>
    api.get('/admin/dashboard/usage-analytics'),

  // Referral codes
  getReferrals: () =>
    api.get('/admin/referrals'),
  createReferral: (data) =>
    api.post('/admin/referrals', data),
  updateReferral: (id, data) =>
    api.put(`/admin/referrals/${id}`, data),
  deleteReferral: (id) =>
    api.delete(`/admin/referrals/${id}`),
  getReferralStudents: (id) =>
    api.get(`/admin/referrals/${id}/students`)
};

// Offers API
export const offerApi = {
  create: (data) =>
    api.post('/offers', data),

  list: () =>
    api.get('/offers'),

  get: (id) =>
    api.get(`/offers/${id}`),

  delete: (id) =>
    api.delete(`/offers/${id}`),

  getStudentOffers: () =>
    api.get('/student/offers'),

  createOrder: (offerId) =>
    api.post('/payments/offer/create-order', { offerId }),

  verifyPayment: (data) =>
    api.post('/payments/offer/verify', data)
};

// Technique-specific chat API
export const techniqueApi = {
  getTechniques: () =>
    api.get('/techniques'),

  getAllTechniques: () =>
    api.get('/techniques/all'),

  getTechnique: (slug) =>
    api.get(`/techniques/${slug}`),

  createTechnique: (data) =>
    api.post('/techniques', data),

  updateTechnique: (id, data) =>
    api.put(`/techniques/${id}`, data),

  deleteTechnique: (id) =>
    api.delete(`/techniques/${id}`),

  sendMessage: (slug, message, conversationId, fileIds = [], comfortLanguage = null, comfortState = null) =>
    api.post(`/techniques/${slug}/chat`, { message, conversationId, fileIds, comfortLanguage, comfortState }),

  getConversations: (slug, limit = 20, offset = 0) =>
    api.get(`/techniques/${slug}/conversations?limit=${limit}&offset=${offset}`),

  getConversation: (slug, id) =>
    api.get(`/techniques/${slug}/conversations/${id}`),

  deleteConversation: (slug, id) =>
    api.delete(`/techniques/${slug}/conversations/${id}`),

  renameConversation: (slug, id, title) =>
    api.patch(`/techniques/${slug}/conversations/${id}`, { title })
};

// File upload API
export const uploadApi = {
  uploadFiles: (files) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getFile: (id) =>
    api.get(`/upload/${id}`),

  getFileContent: (id) =>
    api.get(`/upload/${id}/content`),

  listFiles: (limit = 20, offset = 0) =>
    api.get(`/upload?limit=${limit}&offset=${offset}`),

  deleteFile: (id) =>
    api.delete(`/upload/${id}`),

  downloadFile: (id) =>
    api.get(`/upload/${id}/download`, { responseType: 'blob' })
};

// Share API
export const shareApi = {
  createShare: (conversationId, accessType = 'public', expiresIn = null) =>
    api.post('/share', { conversationId, accessType, expiresIn }),

  getSharedConversation: (token) =>
    api.get(`/share/${token}`),

  listShares: () =>
    api.get('/share'),

  revokeShare: (id) =>
    api.delete(`/share/${id}`),

  updateShare: (id, data) =>
    api.put(`/share/${id}`, data)
};

// Direct messaging API
export const messageApi = {
  getThreads: () =>
    api.get('/messages/threads'),

  createThread: (participantId) =>
    api.post('/messages/threads', { participantId }),

  getThread: (id, limit = 50, before = null) => {
    let url = `/messages/threads/${id}?limit=${limit}`;
    if (before) url += `&before=${before}`;
    return api.get(url);
  },

  sendMessage: (threadId, content) =>
    api.post(`/messages/threads/${threadId}`, { content }),

  markAsRead: (threadId) =>
    api.put(`/messages/threads/${threadId}/read`),

  poll: (since) =>
    api.get(`/messages/poll?since=${encodeURIComponent(since)}`),

  getUnreadCount: () =>
    api.get('/messages/unread-count'),

  getContacts: () =>
    api.get('/messages/contacts')
};

// Channel API (Open Chat)
export const channelApi = {
  getChannels: () =>
    api.get('/channels'),

  createChannel: (name, description, channelType = 'broadcast') =>
    api.post('/channels', { name, description, channelType }),

  getChannel: (id, limit = 50, before = null) => {
    let url = `/channels/${id}?limit=${limit}`;
    if (before) url += `&before=${before}`;
    return api.get(url);
  },

  postMessage: (channelId, content, messageType = 'text') =>
    api.post(`/channels/${channelId}/messages`, { content, messageType }),

  updateChannel: (id, data) =>
    api.put(`/channels/${id}`, data),

  deleteChannel: (id) =>
    api.delete(`/channels/${id}`),

  markAsRead: (id) =>
    api.put(`/channels/${id}/read`),

  addMembers: (channelId, userIds) =>
    api.post(`/channels/${channelId}/members`, { userIds }),

  removeMember: (channelId, userId) =>
    api.delete(`/channels/${channelId}/members/${userId}`),

  pinMessage: (channelId, messageId, pinned = true) =>
    api.put(`/channels/${channelId}/messages/${messageId}/pin`, { pinned }),

  poll: (channelId, since) =>
    api.get(`/channels/${channelId}/poll?since=${encodeURIComponent(since)}`)
};

// LMS API
export const lmsApi = {
  getCourses: () =>
    api.get('/lms/courses'),

  getCourse: (id) =>
    api.get(`/lms/courses/${id}`),

  createCourse: (data) =>
    api.post('/lms/courses', data),

  updateCourse: (id, data) =>
    api.put(`/lms/courses/${id}`, data),

  deleteCourse: (id) =>
    api.delete(`/lms/courses/${id}`),

  getLessons: (courseId) =>
    api.get(`/lms/courses/${courseId}/lessons`),

  getLesson: (id) =>
    api.get(`/lms/lessons/${id}`),

  createLesson: (data) =>
    api.post('/lms/lessons', data),

  // Videos attached to a technique (not a course)
  getTechniqueLessons: (slug) =>
    api.get(`/lms/techniques/${slug}/lessons`),

  updateLesson: (id, data) =>
    api.put(`/lms/lessons/${id}`, data),

  deleteLesson: (id) =>
    api.delete(`/lms/lessons/${id}`),

  updateProgress: (lessonId, data) =>
    api.put(`/lms/lessons/${lessonId}/progress`, data),

  // Downloadable resource links per lesson
  getLessonResources: (lessonId) =>
    api.get(`/lms/lessons/${lessonId}/resources`),

  addLessonResource: (lessonId, data) =>
    api.post(`/lms/lessons/${lessonId}/resources`, data),

  deleteLessonResource: (resourceId) =>
    api.delete(`/lms/lessons/resources/${resourceId}`),

  updateTranscript: (lessonId, transcript) =>
    api.put(`/lms/lessons/${lessonId}/transcript`, { transcript }),

  triggerTranscription: (lessonId, audioFile) => {
    const formData = new FormData();
    if (audioFile) formData.append('audio', audioFile);
    return api.post(`/lms/lessons/${lessonId}/transcribe`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  getLiveSessions: (params = {}) =>
    api.get('/lms/live-sessions', { params }),

  getLiveSession: (id) =>
    api.get(`/lms/live-sessions/${id}`),

  createLiveSession: (data) =>
    api.post('/lms/live-sessions', data),

  updateLiveSession: (id, data) =>
    api.put(`/lms/live-sessions/${id}`, data),

  deleteLiveSession: (id) =>
    api.delete(`/lms/live-sessions/${id}`),

  getMyProgress: () =>
    api.get('/lms/my-progress')
};

// Assessment API
export const assessmentApi = {
  getAssessments: (params = {}) =>
    api.get('/assessments', { params }),

  getAssessment: (id) =>
    api.get(`/assessments/${id}`),

  createAssessment: (data) =>
    api.post('/assessments', data),

  updateAssessment: (id, data) =>
    api.put(`/assessments/${id}`, data),

  deleteAssessment: (id) =>
    api.delete(`/assessments/${id}`),

  // Extract MCQs from a PDF/image. Optionally pass an answer_key file — when
  // provided, the AI uses it as the source of truth for correct_answer instead
  // of guessing from subject knowledge.
  extractQuestionsFromFile: (questionsFile, answerKeyFile) => {
    const formData = new FormData();
    formData.append('questions_file', questionsFile);
    if (answerKeyFile) {
      formData.append('answer_key_file', answerKeyFile);
    }
    return api.post('/assessments/extract-questions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000    // AI extraction can take 30-60s for big PDFs
    });
  },

  addQuestion: (assessmentId, data) =>
    api.post(`/assessments/${assessmentId}/questions`, data),

  updateQuestion: (questionId, data) =>
    api.put(`/assessments/questions/${questionId}`, data),

  deleteQuestion: (questionId) =>
    api.delete(`/assessments/questions/${questionId}`),

  startAttempt: (assessmentId) =>
    api.post(`/assessments/${assessmentId}/attempt`),

  submitAttempt: (attemptId, responses) =>
    api.put(`/assessments/attempts/${attemptId}`, { responses }),

  getAttempt: (attemptId) =>
    api.get(`/assessments/attempts/${attemptId}`),

  getAttempts: (assessmentId) =>
    api.get(`/assessments/${assessmentId}/attempts`),

  gradeAttempt: (attemptId, data) =>
    api.put(`/assessments/attempts/${attemptId}/grade`, data),

  getMyAttempts: () =>
    api.get('/assessments/my-attempts')
};

// Submission API
export const submissionApi = {
  create: (data) => {
    const formData = new FormData();
    formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.lessonId) formData.append('lessonId', data.lessonId);
    if (data.assessmentId) formData.append('assessmentId', data.assessmentId);
    if (data.files) {
      for (const file of data.files) {
        formData.append('files', file);
      }
    }
    return api.post('/submissions', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  list: (params = {}) =>
    api.get('/submissions', { params }),

  get: (id) =>
    api.get(`/submissions/${id}`),

  update: (id, data) =>
    api.put(`/submissions/${id}`, data),

  delete: (id) =>
    api.delete(`/submissions/${id}`),

  review: (id, data) =>
    api.put(`/submissions/${id}/review`, data),

  downloadFile: (submissionId, fileId) =>
    api.get(`/submissions/${submissionId}/files/${fileId}/download`, { responseType: 'blob' })
};

// Coupon API
export const couponApi = {
  list: () =>
    api.get('/coupons'),

  create: (data) =>
    api.post('/coupons', data),

  update: (id, data) =>
    api.put(`/coupons/${id}`, data),

  delete: (id) =>
    api.delete(`/coupons/${id}`),

  validate: (code, amount) =>
    api.post('/coupons/validate', { code, amount })
};

// Knowledge Base API (admin)
export const knowledgeApi = {
  getAll: () =>
    api.get('/admin/knowledge'),

  getStats: () =>
    api.get('/admin/knowledge/stats'),

  getTechniques: () =>
    api.get('/admin/knowledge/techniques'),

  create: (data) =>
    api.post('/admin/knowledge', data),

  update: (id, data) =>
    api.put(`/admin/knowledge/${id}`, data),

  remove: (id) =>
    api.delete(`/admin/knowledge/${id}`),

  toggle: (id) =>
    api.put(`/admin/knowledge/${id}/toggle`),

  upload: (file, techniqueId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (techniqueId) formData.append('technique_id', techniqueId);
    return api.post('/admin/knowledge/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  saveChunks: (data) =>
    api.post('/admin/knowledge/upload/save', data),

  test: (message) =>
    api.post('/admin/knowledge/test', { message }),

  testEntry: (id, message) =>
    api.post(`/admin/knowledge/${id}/test`, { message })
};

// Auth API
export const authApi = {
  getMe: () =>
    api.get('/auth/me'),
  changePassword: (currentPassword, newPassword) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),
  completeOnboarding: () =>
    api.post('/auth/complete-onboarding'),
  saveOnboardingProfile: (data) =>
    api.post('/auth/save-onboarding-profile', data),
  validateReferral: (code) =>
    api.get(`/auth/validate-referral/${encodeURIComponent(code)}`)
};

// Notification API
export const notificationApi = {
  list: (params = {}) =>
    api.get('/notifications', { params }),

  getUnreadCount: () =>
    api.get('/notifications/unread-count'),

  markRead: (id) =>
    api.put(`/notifications/${id}/read`),

  markAllRead: () =>
    api.put('/notifications/read-all'),

  delete: (id) =>
    api.delete(`/notifications/${id}`),

  getPreferences: () =>
    api.get('/notifications/preferences'),

  updatePreferences: (data) =>
    api.put('/notifications/preferences', data)
};
